import { PerStyleVariationMapBuilder } from "@faire/retailer-visitor-shared/features/OpenSizing/PerStyleVariationMapBuilder";
import { getPrePackSize } from "@faire/retailer-visitor-shared/features/PrePacks/getPrePackSize";
import { isPrePackProductOption } from "@faire/retailer-visitor-shared/features/PrePacks/isPrePackProduct";
import {
  isDiscontinued,
  isProductOptionInStock,
} from "@faire/retailer-visitor-shared/lib/productOptions";
import { getRequiredProductCustomizationsAdditionalCharge } from "@faire/retailer-visitor-shared/services/ProductCustomization";
import { ProductDetails } from "@faire/retailer-visitor-shared/views/product/ProductDetails/model";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { IProductOption } from "@faire/web-api--source/indigofair/data/IProductOption";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { logAssert } from "@faire/web/common/logging";
import { formatMoney } from "@faire/web/common/money";

const productWholesalePriceMoney = (
  product: ProductDetails.IProduct,
  selectedOption?: IProductOption,
  perStyleOptionVariation?: PerStyleVariationMapBuilder.IPerStyleOptionVariation[]
): IFaireMoney | null => {
  if (
    !selectedOption ||
    isDiscontinued(product, selectedOption) ||
    !selectedOption.wholesale_price?.amount_cents
  ) {
    return null;
  }

  let defaultOptionAmountCents =
    selectedOption.wholesale_promo_price?.discounted_item_price?.amount_cents ??
    selectedOption.wholesale_price.amount_cents;

  // we want to show the minimum of all options within the same style variant for open sizing.
  // using a reduce but the 'accumulator' will be the min price through math.min.
  if (perStyleOptionVariation) {
    const minVariationPrice = perStyleOptionVariation.reduce(
      (minPrice, variation) =>
        Math.min(minPrice, variation?.wholesalePrice?.amount_cents ?? Infinity),
      Infinity
    );

    defaultOptionAmountCents = Math.min(
      minVariationPrice,
      defaultOptionAmountCents
    );
  }

  let price = IFaireMoney.build({
    amount_cents:
      defaultOptionAmountCents +
      (getRequiredProductCustomizationsAdditionalCharge(
        product.customizations || []
      )?.amount_cents ?? 0),
    currency: selectedOption.wholesale_price.currency,
  });

  if (isPrePackProductOption(selectedOption)) {
    const prePackUnitCount = getPrePackSize(selectedOption.sizing_quantity);
    logAssert(
      prePackUnitCount !== 0 || !isNaN(prePackUnitCount),
      "Prepack unit count is invalid",
      {
        data: { selectedOption },
      }
    );
    price = IFaireMoney.build({
      ...price,
      amount_cents: price.amount_cents! / prePackUnitCount,
    });
  }

  return price;
};

export const productWholesalePrice = (
  strictLocalize: StrictLocalizeFunction,
  product: ProductDetails.IProduct,
  showTextWhenOutOfStock: boolean,
  selectedOption?: IProductOption,
  perStyleOptionVariation?: PerStyleVariationMapBuilder.IPerStyleOptionVariation[]
): string | null => {
  if (selectedOption === undefined || isDiscontinued(product, selectedOption)) {
    return strictLocalize({
      id: "Zh2Kkv",
      defaultMessage: "(Unavailable)",
      description: { text: "the product is not available for purchase" },
    });
  }

  if (
    !isProductOptionInStock(selectedOption, perStyleOptionVariation) &&
    showTextWhenOutOfStock
  ) {
    return strictLocalize({
      id: "4dUIWa",
      defaultMessage: "(Out of stock)",
      description: { text: "the product is out of stock" },
    });
  }

  const price = productWholesalePriceMoney(
    product,
    selectedOption,
    perStyleOptionVariation
  );

  if (!price) {
    return null;
  }

  return formatMoney({
    money: price,
  });
};
